import { IProcessedMeasurement, IProcessedMeasurementError, IVitalParameterMeasurement } from 'app/types/measurement';
import { createMeasurement } from '../shared/factories/MeasurementFactory';
import { validateMeasurementPayload } from '@shared/utils/measurementPayload';
import { normalizeAlert, formatMeasurementValue } from '@shared/utils/webSocketUtils';
import { Telemetry } from './telemetry/Telemetry';

export const processErrorMessage = (data: unknown): IProcessedMeasurementError | null => {
  if (!data || typeof data !== 'object') return null;

  const frame = data as Record<string, unknown>;
  if (frame.has_error !== true || typeof frame.activity_name !== 'string') return null;

  Telemetry.event('measurement.device_error.received', {
    domain: 'measurement',
    activityName: frame.activity_name,
  }, 'warning');

  return {
    activityName: frame.activity_name,
    receivedAt: Date.now(),
  };
};

export const processMessage = (data: unknown): IProcessedMeasurement | null => {
  const payload = validateMeasurementPayload(data);
  if (!payload) return null;

  try {
    const measurementData: IVitalParameterMeasurement = {
      activity_id: payload.activity_id,
      activity_type: payload.activity_type,
      activity_name: payload.activity_name as IVitalParameterMeasurement['activity_name'],
      status: payload.status,
      completed_at: payload.completed_at,
      measurement: payload.measurement as unknown as IVitalParameterMeasurement['measurement'],
      standalone: payload.standalone,
      alert: null,
    };

    const value = createMeasurement(measurementData);
    if (value === null) return null;

    const alert = normalizeAlert(payload.alert);

    return {
      id: payload.measurement.id,
      type: payload.measurement.type,
      value,
      measuredAt: payload.measurement.measured_at,
      deviceId: payload.measurement.device_id,
      deviceModel: payload.measurement.device_model,
      isManual: payload.measurement.manual_measurement ?? false,
      hasAlert: alert !== null,
      status: payload.status,
      activityName: payload.activity_name,
      activityId: payload.activity_id,
      activityType: payload.activity_type,
      standalone: payload.standalone,
      rawMeasurement: payload.measurement as Record<string, any>,
      alert,
      unlocksActivityId: payload.unlocks_activity_id ?? null,
      unlocksActivityAt: payload.unlocks_activity_at ?? null,
    };
  } catch (error) {
    Telemetry.error(error as Error, { event: 'net.websocket.measurement.process.fail', domain: 'net', channel: 'measurement' });
    return null;
  }
};

export { formatMeasurementValue as formatValue };
